import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography, FormHelperText } from "@mui/material";
import HandshakeTwoToneIcon from "@mui/icons-material/HandshakeTwoTone";
import {
  Textarea,
  FormControl as FinanceableFormControl,
  FormHelperText as FinanceableFormHelperText,
} from "@financeable-com-au/financeable-ui";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import {
  anyForeseeableChangesImpactingLoanRepaymentDetail,
  explainWhyIsThisLoanNotUnsuitableForTheApplicantDetail,
  anyForeseeableChangesImpactingLoanRepaymentOption,
  APPLICATION_SECTIONS_ID,
  balloonPaymentAtTheEndOfTheLoanDetail,
  howApplicantIntendToDoLoanRepaymentAfterChangeDetail,
  increaseInLoanAmountForFinancingCostOfInsuranceDetail,
  whatAreYouLookingForInYourLoanDetail,
  whatAreYouLookingForInYourLoanOption,
  whatIsThePurposeOfThisApplicationDetail,
  whatIsThePurposeOfThisApplicationOption,
  whatToDoWithBalloonPaymentAtTheEndOfTheLoanTermDetail,
  whatToDoWithBalloonPaymentAtTheEndOfTheLoanTermOption,
  whyDoesThisLoanTermSuitYourRequirementsDetail,
  whyDoesThisLoanTermSuitYourRequirementsOption,
} from "../../constants";
import {
  loanDetailsSelector,
  updateNccpDeclaration,
  updateFirstCustomersDisclaimer,
  setValidationRequireFieldError,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import RadioButtonGroup from "../Utils/RadioButtonGroup";

const NCCPDeclaration = ({
  whatIsThePurposeOfThisApplication,
  whyDoesThisLoanTermSuitYourRequirements,
  whatAreYouLookingForInYourLoan,
  anyForeseeableChangesImpactingLoanRepayment,
  howApplicantIntendToDoLoanRepaymentAfterChange,
  whyIsThisLoanNotUnsuitableForTheApplicantDetail,
  increaseInLoanAmountForFinancingCostOfInsurance,
  balloonPaymentAtTheEndOfTheLoan,
  whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm,
  application,
  customerId,
  isDisableEdit,
}) => {
  const dispatch = useDispatch();
  const { balloon } = useSelector(loanDetailsSelector);
  const { validationRequireFieldError } = useSelector(userDetailsSelector);
  const agreementError = validationRequireFieldError?.agreementError ?? {};

  const isPersonalLoan = application?.applicationType === ApplicationType.Personal;

  const [nccpDeclarationDetail, setNCCPDeclarationDetail] = useState({
    howApplicantIntendToDoLoanRepaymentAfterChange: howApplicantIntendToDoLoanRepaymentAfterChange || "",
    whyIsThisLoanNotUnsuitableForTheApplicantDetail: whyIsThisLoanNotUnsuitableForTheApplicantDetail || "",
    whatIsThePurposeOfThisApplication: whatIsThePurposeOfThisApplication || "",
    whyDoesThisLoanTermSuitYourRequirements: whyDoesThisLoanTermSuitYourRequirements || "",
    whatAreYouLookingForInYourLoan: whatAreYouLookingForInYourLoan || "",
    anyForeseeableChangesImpactingLoanRepayment: anyForeseeableChangesImpactingLoanRepayment || "",
    increaseInLoanAmountForFinancingCostOfInsurance: increaseInLoanAmountForFinancingCostOfInsurance || "",
    balloonPaymentAtTheEndOfTheLoan: balloonPaymentAtTheEndOfTheLoan || "",
    whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm: whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm || "",
  });

  const nccpDeclarationDetailRef = useRef(nccpDeclarationDetail);
  useEffect(() => {
    nccpDeclarationDetailRef.current = nccpDeclarationDetail;
  }, [nccpDeclarationDetail]);

  const clearAgreementRequiredFieldError = (fieldName, value) => {
    if (!value?.trim()) {
      return;
    }

    const updatedAgreementError = { ...agreementError };
    delete updatedAgreementError[fieldName];

    dispatch(
      setValidationRequireFieldError({
        ...validationRequireFieldError,
        agreementError: updatedAgreementError,
      }),
    );
  };

  const createTextareaHandle = (fieldName) => ({
    onChange: (event) => {
      setNCCPDeclarationDetail((prev) => ({ ...prev, [fieldName]: event.target.value }));
    },
    onBlur: (event) => {
      const latest = nccpDeclarationDetailRef.current;
      dispatch(
        updateFirstCustomersDisclaimer({
          [fieldName]: event.target.value,
        }),
      );
      clearAgreementRequiredFieldError(fieldName, event.target.value);
      dispatch(
        updateNccpDeclaration({
          ...latest,
          [fieldName]: event.target.value,
          application: application._id,
          customer: customerId,
        }),
      );
    },
  });

  const handleWhyNotUnsuitable = createTextareaHandle("whyIsThisLoanNotUnsuitableForTheApplicantDetail");
  const handleHowRepayment = createTextareaHandle("howApplicantIntendToDoLoanRepaymentAfterChange");

  const handleNCCPDeclaration = (name, value) => {
    setNCCPDeclarationDetail({
      ...nccpDeclarationDetail,
      [name]: value,
    });
    dispatch(
      updateFirstCustomersDisclaimer({
        [name]: value,
      }),
    );
    dispatch(
      updateNccpDeclaration({
        ...nccpDeclarationDetail,
        [name]: value,
        application: application._id,
        customer: customerId,
      }),
    );
  };

  const { getAgreementLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldName, localFieldName }) =>
    getAgreementLocalAndLenderError({ fieldName, localFieldName, localState: nccpDeclarationDetail });

  return (
    <Grid
      container
      item
      xs={12}
      style={{
        margin: "0 0 30px",
      }}
      id={APPLICATION_SECTIONS_ID.NCCP_DECLARATION}
    >
      <Grid container xs={12} sm={12} md={2}>
        <Grid item xs={12} sm={12} md={2}>
          <HandshakeTwoToneIcon style={{ marginRight: "2px" }} />
          <Typography>NCCP Declaration</Typography>
        </Grid>
      </Grid>

      <Grid
        xs={12}
        sm={12}
        md={10}
        container
        spacing={4}
        style={{
          padding: "0 0 0 10px",
        }}
      >
        <Grid item md={12}>
          <Typography variant="h6">
            Please ask the following questions to the applicant(s) and provide answers.
          </Typography>
        </Grid>

        {!isPersonalLoan && (
          <Grid item md={12}>
            <span>{whatIsThePurposeOfThisApplicationDetail}</span>
            <Grid item md={12} sx={{ marginTop: "10px" }}>
              <FormControl
                fullWidth
                variant="filled"
                error={getLocalAndLenderError({ fieldName: "whatIsThePurposeOfThisApplication" })}
              >
                <InputLabel id={`purpose-of-this-application-label-${nccpDeclarationDetail?._id}`}>
                  Purpose of this application
                </InputLabel>
                <Select
                  size="small"
                  labelId={`purpose-of-this-application-label-${nccpDeclarationDetail?._id}`}
                  disabled={isDisableEdit}
                  id={`purpose-of-this-application-select-${nccpDeclarationDetail?._id}`}
                  value={nccpDeclarationDetail.whatIsThePurposeOfThisApplication}
                  label="Purpose Of This Application"
                  onChange={(event) => {
                    handleNCCPDeclaration("whatIsThePurposeOfThisApplication", event.target.value);
                  }}
                >
                  {whatIsThePurposeOfThisApplicationOption.map((i) => (
                    <MenuItem value={i}>{i}</MenuItem>
                  ))}
                </Select>
                {getLocalAndLenderError({ fieldName: "whatIsThePurposeOfThisApplication" }) && (
                  <FormHelperText>
                    {getLocalAndLenderError({ fieldName: "whatIsThePurposeOfThisApplication" })}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        )}

        <Grid item md={12}>
          <span style={{ marginBottom: "20px" }}>{whyDoesThisLoanTermSuitYourRequirementsDetail}</span>
          <Grid item md={12} sx={{ marginTop: "10px" }}>
            <FormControl
              fullWidth
              variant="filled"
              error={getLocalAndLenderError({ fieldName: "whyDoesThisLoanTermSuitYourRequirements" })}
            >
              <InputLabel id={`loan-term-suit-label-${nccpDeclarationDetail?._id}`}>Loan term suit</InputLabel>
              <Select
                size="small"
                labelId={`loan-term-suit-label-${nccpDeclarationDetail?._id}`}
                id={`loan-term-suit-select-${nccpDeclarationDetail?._id}`}
                value={nccpDeclarationDetail?.whyDoesThisLoanTermSuitYourRequirements}
                disabled={isDisableEdit}
                label="Loan Term Suit Your Requirements"
                onChange={(event) => {
                  handleNCCPDeclaration("whyDoesThisLoanTermSuitYourRequirements", event.target.value);
                }}
              >
                {whyDoesThisLoanTermSuitYourRequirementsOption.map((i) => (
                  <MenuItem value={i}>{i}</MenuItem>
                ))}
              </Select>
              {getLocalAndLenderError({ fieldName: "whyDoesThisLoanTermSuitYourRequirements" }) && (
                <FormHelperText>
                  {getLocalAndLenderError({ fieldName: "whyDoesThisLoanTermSuitYourRequirements" })}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid item md={12}>
          <span style={{ marginBottom: "20px" }}>{whatAreYouLookingForInYourLoanDetail}</span>
          <Grid item md={12} sx={{ marginTop: "10px" }}>
            <FormControl
              fullWidth
              variant="filled"
              error={getLocalAndLenderError({ fieldName: "whatAreYouLookingForInYourLoan" })}
            >
              <InputLabel id={`loan-detail-label-${nccpDeclarationDetail?._id}`}>LoanDetail</InputLabel>
              <Select
                size="small"
                labelId={`loan-detail-label-${nccpDeclarationDetail?._id}`}
                id={`loan-detail-select-${nccpDeclarationDetail?._id}`}
                disabled={isDisableEdit}
                value={nccpDeclarationDetail?.whatAreYouLookingForInYourLoan}
                label="Looking For In Your LoanDetail"
                onChange={(event) => {
                  handleNCCPDeclaration("whatAreYouLookingForInYourLoan", event.target.value);
                }}
              >
                {whatAreYouLookingForInYourLoanOption.map((i) => (
                  <MenuItem value={i}>{i}</MenuItem>
                ))}
              </Select>
              {getLocalAndLenderError({ fieldName: "whatAreYouLookingForInYourLoan" }) && (
                <FormHelperText>
                  {getLocalAndLenderError({ fieldName: "whatAreYouLookingForInYourLoan" })}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid item md={12}>
          <span style={{ marginBottom: "20px" }}>{anyForeseeableChangesImpactingLoanRepaymentDetail}</span>
          <Grid item md={12} sx={{ marginTop: "10px" }}>
            <FormControl
              fullWidth
              variant="filled"
              error={getLocalAndLenderError({ fieldName: "anyForeseeableChangesImpactingLoanRepayment" })}
            >
              <InputLabel id={`loan-repayment-detail-label-${nccpDeclarationDetail?._id}`}>
                Loan repayment detail
              </InputLabel>

              <Select
                size="small"
                labelId={`loan-repayment-detail-label-${nccpDeclarationDetail?._id}`}
                id={`loan-repayment-detail-select-${nccpDeclarationDetail?._id}`}
                value={nccpDeclarationDetail?.anyForeseeableChangesImpactingLoanRepayment}
                disabled={isDisableEdit}
                label="Foreseeable Changes Impacting Loan Repayment Detail"
                onChange={(event) => {
                  handleNCCPDeclaration("anyForeseeableChangesImpactingLoanRepayment", event.target.value);
                }}
              >
                {anyForeseeableChangesImpactingLoanRepaymentOption.map((i) => (
                  <MenuItem value={i}>{i}</MenuItem>
                ))}
              </Select>
              {getLocalAndLenderError({ fieldName: "anyForeseeableChangesImpactingLoanRepayment" }) && (
                <FormHelperText>
                  {getLocalAndLenderError({ fieldName: "anyForeseeableChangesImpactingLoanRepayment" })}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid item md={12}>
          <div style={{ marginBottom: "12px" }}>{explainWhyIsThisLoanNotUnsuitableForTheApplicantDetail}</div>
          <FinanceableFormControl
            fullWidth
            error={!!getLocalAndLenderError({ fieldName: "whyIsThisLoanNotUnsuitableForTheApplicantDetail" })}
          >
            <Textarea
              minRows={8}
              name="whyIsThisLoanNotUnsuitableForTheApplicantDetail"
              placeholder="Type your notes here..."
              size="md"
              variant={
                !!getLocalAndLenderError({ fieldName: "whyIsThisLoanNotUnsuitableForTheApplicantDetail" })
                  ? "outlined"
                  : "soft"
              }
              value={nccpDeclarationDetail?.whyIsThisLoanNotUnsuitableForTheApplicantDetail}
              onChange={handleWhyNotUnsuitable.onChange}
              onBlur={handleWhyNotUnsuitable.onBlur}
              disabled={isDisableEdit}
            />
            <FinanceableFormHelperText>
              {getLocalAndLenderError({ fieldName: "whyIsThisLoanNotUnsuitableForTheApplicantDetail" })}
            </FinanceableFormHelperText>
          </FinanceableFormControl>
        </Grid>

        <Grid item md={12}>
          <div style={{ marginBottom: "12px" }}>{howApplicantIntendToDoLoanRepaymentAfterChangeDetail}</div>
          <FinanceableFormControl
            fullWidth
            error={!!getLocalAndLenderError({ fieldName: "howApplicantIntendToDoLoanRepaymentAfterChange" })}
          >
            <Textarea
              minRows={8}
              name="howApplicantIntendToDoLoanRepaymentAfterChange"
              placeholder="Type your notes here..."
              size="md"
              variant={
                !!getLocalAndLenderError({ fieldName: "howApplicantIntendToDoLoanRepaymentAfterChange" })
                  ? "outlined"
                  : "soft"
              }
              value={nccpDeclarationDetail?.howApplicantIntendToDoLoanRepaymentAfterChange}
              onChange={handleHowRepayment.onChange}
              onBlur={handleHowRepayment.onBlur}
              disabled={isDisableEdit}
            />
            <FinanceableFormHelperText>
              {getLocalAndLenderError({ fieldName: "howApplicantIntendToDoLoanRepaymentAfterChange" })}
            </FinanceableFormHelperText>
          </FinanceableFormControl>
        </Grid>

        <Grid item md={12}>
          <span style={{ marginBottom: "20px" }}>{increaseInLoanAmountForFinancingCostOfInsuranceDetail}</span>
          <Grid item md={5}>
            <RadioButtonGroup
              required
              disabled={isDisableEdit}
              value={nccpDeclarationDetail?.increaseInLoanAmountForFinancingCostOfInsurance}
              name={"increaseInLoanAmountForFinancingCostOfInsurance"}
              handleValue={(value) => {
                handleNCCPDeclaration("increaseInLoanAmountForFinancingCostOfInsurance", value);
              }}
              options={[
                { icon: "Yes", label: "Yes" },
                { icon: "No", label: "No" },
              ]}
              optionStyle={{
                ...(getLocalAndLenderError({ fieldName: "increaseInLoanAmountForFinancingCostOfInsurance" }) && {
                  borderColor: "#d32f2f",
                }),
              }}
            />
            {getLocalAndLenderError({ fieldName: "increaseInLoanAmountForFinancingCostOfInsurance" }) && (
              <FormHelperText error={true}>
                {getLocalAndLenderError({ fieldName: "increaseInLoanAmountForFinancingCostOfInsurance" })}
              </FormHelperText>
            )}
          </Grid>
        </Grid>

        {!isPersonalLoan && (balloon ?? 0) > 0 && (
          <Grid item md={12}>
            <span style={{ marginBottom: "20px" }}>{balloonPaymentAtTheEndOfTheLoanDetail}</span>
            <Grid item md={5}>
              <RadioButtonGroup
                required
                disabled={isDisableEdit}
                value={nccpDeclarationDetail?.balloonPaymentAtTheEndOfTheLoan}
                name={"balloonPaymentAtTheEndOfTheLoan"}
                handleValue={(value) => {
                  handleNCCPDeclaration("balloonPaymentAtTheEndOfTheLoan", value);
                }}
                options={[
                  { icon: "Yes", label: "Yes" },
                  { icon: "No", label: "No" },
                ]}
                optionStyle={{
                  ...(getLocalAndLenderError({ fieldName: "balloonPaymentAtTheEndOfTheLoan" }) && {
                    borderColor: "#d32f2f",
                  }),
                }}
              />
              {getLocalAndLenderError({ fieldName: "balloonPaymentAtTheEndOfTheLoan" }) && (
                <FormHelperText error={true}>
                  {getLocalAndLenderError({ fieldName: "balloonPaymentAtTheEndOfTheLoan" })}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        )}

        {!isPersonalLoan && (balloon ?? 0) > 0 && (
          <Grid item md={12}>
            <span style={{ marginBottom: "20px" }}>{whatToDoWithBalloonPaymentAtTheEndOfTheLoanTermDetail}</span>
            <Grid item md={12} sx={{ marginTop: "10px" }}>
              <FormControl
                fullWidth
                variant="filled"
                error={getLocalAndLenderError({ fieldName: "whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm" })}
              >
                <InputLabel id={`balloon-payment-label-${nccpDeclarationDetail?._id}`}>Balloon payment</InputLabel>
                <Select
                  size="small"
                  labelId={`balloon-payment-label-${nccpDeclarationDetail?._id}`}
                  disabled={isDisableEdit}
                  id={`balloon-payment-select-${nccpDeclarationDetail?._id}`}
                  value={nccpDeclarationDetail?.whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm}
                  label="what To Do With Balloon Payment At The End Of The Loan Term"
                  onChange={(event) => {
                    handleNCCPDeclaration("whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm", event.target.value);
                  }}
                >
                  {whatToDoWithBalloonPaymentAtTheEndOfTheLoanTermOption.map((i) => (
                    <MenuItem value={i}>{i}</MenuItem>
                  ))}
                </Select>
                {getLocalAndLenderError({ fieldName: "whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm" }) && (
                  <FormHelperText>
                    {getLocalAndLenderError({ fieldName: "whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm" })}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default NCCPDeclaration;
